import { useEffect, useState } from 'react';
import Icon from './Icon';

// The supplier field's label and "what the AI read" — admin only. The printed
// name and ח.פ. as read at upload, and how the upload recognised the supplier
// when it did (utils/ocrSupplierRead.js). The read is often garbled ("אשיg");
// seeing it is how the admin tells a misread from a supplier the system doesn't
// know.
//
// So it only tucks away when it has nothing to warn about (#359): while the
// upload's match is still the chosen supplier, the read opens on hover over the
// label, on keyboard focus of the ⓘ and on a tap of it (a phone has no hover).
// With no recognised match it stays on the label row, in plain sight.
//
// Hover is state, not a CSS :hover, so Escape and a closing click can beat it:
// once dismissed, the read stays shut until the mouse leaves and comes back.
export default function OcrSupplierReadLine({ show, read, label }) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const open = pinned || (hovered && !dismissed);

  const dismiss = () => {
    setPinned(false);
    setDismissed(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = e => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!show || !read) return <label>{label}</label>;

  const parts = [];
  if (read.name) parts.push(<span key="n">נקרא מהמסמך: <bdi>{read.name}</bdi></span>);
  if (read.businessId) parts.push(<span key="b" className="ocr-supplier-read-part">ח.פ. <bdi dir="ltr">{read.businessId}</bdi></span>);
  if (read.matchedBy) parts.push(<span key="m" className="ocr-supplier-read-part">זוהה לפי {read.matchedBy}</span>);
  const text = parts.flatMap((p, i) => (i ? [<span key={`s${i}`} aria-hidden="true"> · </span>, p] : [p]));

  if (!read.matchedBy) {
    return (
      <div className="ocr-read-label">
        <label>{label}</label>
        <p className="ocr-supplier-read">{text}</p>
      </div>
    );
  }

  return (
    <div className="ocr-read-label">
      <span
        className={`ocr-read-tip${open ? ' open' : ''}`}
        onPointerEnter={e => {
          if (e.pointerType !== 'mouse') return;
          setHovered(true);
          setDismissed(false);
        }}
        onPointerLeave={e => { if (e.pointerType === 'mouse') setHovered(false); }}
      >
        <label>{label}</label>
        <button
          type="button"
          className="stat-info-trigger"
          aria-label="מה נקרא מהמסמך"
          aria-expanded={open}
          onClick={() => { if (pinned) dismiss(); else setPinned(true); }}
          onFocus={e => { if (e.currentTarget.matches(':focus-visible')) setPinned(true); }}
          onBlur={() => setPinned(false)}
        >
          <Icon name="info" size={13} />
        </button>
        <span className="ocr-read-pop" role="note">{text}</span>
      </span>
      {pinned && <div className="menu-backdrop" onClick={() => setPinned(false)} />}
    </div>
  );
}
